import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {SessionStorage} from 'ngx-webstorage';
import {Item} from '../../project/shared/project-model';
import {TreeNode} from "primeng/api";
import {UtilService} from '../../shared/util/util.service';
import {RangeType} from '../../shared/model/range-type';
import {MySEStatusEnum} from '../../shared/model/mySEStatusEnum';
import {ComponentType} from '../../configuration/shared/model/component-type';
import {LoggerService} from '../../shared/logging/logger.service';
import {DeliveryPolicy} from '../../shared/model/deliveryPolicy';
import {ReferenceBom} from './bom-model';
import {BomService} from './bom.service';
import {SwitchBoardComponent} from '../../configuration/shared/model/component';
import {SimplifiedRange} from '../../shared/model/simplifiedRange-model';
import {IProduct} from '../../configuration/shared/model/IProduct';
import {OfferService} from '../../offers/shared/offer.service';
import {Range} from '../../shared/model/range-model';
import {RightsService} from "../../shared/rights/rights.service";

@Injectable()
export class BomTreeDataHelper {

  @SessionStorage()
  localization;

  constructor(private bomService: BomService,
              private logger: LoggerService,
              private offerService: OfferService,
              private translateService: TranslateService,
              private rightsService: RightsService) {
  }

  private static createTreeNode(category): TreeNode {
    return {
      data: {
        category: category,
        totalPrice: null,
        totalNetPrice: null,
        discountedPrice: null,
        discountedNetPrice: null,
        partnerDiscount: null,
        mySEStatus: MySEStatusEnum.NOT_APPLICABLE,
        optionCategory: false
      },
      children: []
    };
  }

  /**
   * Create a TreeNode on BOM from the item
   * @param item the itemCT
   * @param ranges list of known ranges for offer
   */
  extractReferencesToTree(item: Item, ranges: Range[]): TreeNode[] {
    this.logger.info('Bom extractReferencesToTree()');
    const myItem = UtilService.clone(item);
    const references: TreeNode[] = [];

    if (!!myItem.range && myItem.range.rangeType === RangeType.TRANSFORMER && myItem.components && myItem.components.length > 0) {
      const transformerNode: TreeNode = BomTreeDataHelper.createTreeNode(this.translateService.instant('T_BOM_TRANSFORMER'));
      const quantity = myItem.components[0].quantity ? myItem.components[0].quantity : 1;
      references.push(transformerNode);
      this.extractReferenceToTreeNode(transformerNode, myItem.components[0], item.discount, myItem.deliveryPolicy, item, ranges);
      this.setExtractPrices(transformerNode, myItem.components[0], quantity, item.discount);

      // Add tree node for transformer options
      const options: TreeNode = BomTreeDataHelper.createTreeNode(this.translateService.instant('T_BOM_TRANSFORMER_OPTIONS'));
      transformerNode.children.push(options);

      this.extractOptionsToTreeNode(myItem.deliveryPolicy, transformerNode, options, options, myItem.components[0], item, ranges);
      // If we have no transform options, we remove the node
      if (options.children.length === 0) {
        const index = transformerNode.children.findIndex(elem => elem.data.category === options.data.category);
        transformerNode.children.splice(index, 1);
      }

    } else if (!!myItem.packageOfferId || myItem.range.rangeType === RangeType.SWITCHBOARD || myItem.range.rangeType === RangeType.BUDJETARY_QUOTE) {
      let componentIdx = 0;
      myItem.components.forEach(component => {
        componentIdx++;
        const quantity = component.quantity ? component.quantity : 1;
        const cubicleNode: TreeNode = BomTreeDataHelper.createTreeNode(this.getTitleItemBom(myItem.range, component, componentIdx));
        references.push(cubicleNode);

        this.extractReferenceToTreeNode(cubicleNode, component, item.discount, myItem.deliveryPolicy, item, ranges);
        this.setExtractPrices(cubicleNode, component, quantity, item.discount);
        let treeNodeParent: TreeNode = null;

        const options: TreeNode = BomTreeDataHelper.createTreeNode(this.translateService.instant('T_BOM_CUBICLE_OPTIONS'));
        treeNodeParent = options;
        cubicleNode.children.push(options);
        if (component.type === ComponentType.CUBICLE) {
          component.functionalUnits.forEach(functionalUnit => {
              const optionFunction: TreeNode = BomTreeDataHelper.createTreeNode(
                'Function ' + this.translateService.instant(functionalUnit['name'])
              );
              cubicleNode.children.push(optionFunction);
              if (functionalUnit.transformer) {
                const transformerNode: TreeNode = BomTreeDataHelper.createTreeNode(
                  this.translateService.instant('T_BOM_TRANSFORMER') + ' ' +
                  this.translateService.instant(functionalUnit.transformer.name));
                this.extractReferenceToTreeNode(transformerNode, functionalUnit.transformer,
                  item.discount, myItem.deliveryPolicy, item, ranges);
                references.push(transformerNode);
                const transformerQuantity = functionalUnit.transformer.quantity ? functionalUnit.transformer.quantity : 1;

                this.setExtractPrices(transformerNode, functionalUnit.transformer, transformerQuantity, item.discount);

                // Add tree node for transformer options
                const transformerOptions: TreeNode = BomTreeDataHelper.createTreeNode(
                  this.translateService.instant('T_BOM_TRANSFORMER_OPTIONS'));
                transformerNode.children.push(transformerOptions);
                treeNodeParent = transformerOptions;

                this.extractOptionsToTreeNode(myItem.deliveryPolicy, transformerNode, treeNodeParent,transformerOptions, functionalUnit.transformer, item, ranges);
                // If we have no transform options, we remove the node
                if (transformerOptions.children.length === 0) {
                  const index = transformerNode.children.findIndex(elem => elem.data.category === transformerOptions.data.category);
                  transformerNode.children.splice(index, 1);
                }
              }

              if (!optionFunction.children || optionFunction.children.length === 0) {
                cubicleNode.children.pop();
              } else {
                if (optionFunction.data.totalPrice != null) {
                  cubicleNode.data.totalPrice += optionFunction.data.totalPrice;
                }
                if (optionFunction.data.totalNetPrice != null) {
                  cubicleNode.data.totalNetPrice += optionFunction.data.totalNetPrice;
                }
                this.setExtractDiscountedPrices(cubicleNode, item.discount);
              }
            }
          );
        }

        this.extractOptionsToTreeNode(myItem.deliveryPolicy, cubicleNode, options, treeNodeParent, component, item, ranges);

        if (options.children.length === 0) {
          const index = cubicleNode.children.findIndex(elem => elem.data.category === options.data.category);
          cubicleNode.children.splice(index, 1);
        }
      });
    }

    const accessories: TreeNode = BomTreeDataHelper.createTreeNode(this.translateService.instant('T_BOM_ACCESSORIES'));
    references.push(accessories);

    let mySENetStatusGlobalAccessories = MySEStatusEnum.NOT_APPLICABLE;
    myItem.accessories.forEach(accessory => {
      this.extractReferenceToTreeNode(accessories, accessory, item.discount, myItem.deliveryPolicy, item, ranges);
      this.setExtractPrices(accessories, accessory, accessory.quantity, item.discount, accessories.data.totalPrice, accessories.data.totalNetPrice);

      if (!UtilService.isValidMySEPriceFromStatus(accessories.data.mySeNetPriceStatus)) {
        mySENetStatusGlobalAccessories = accessories.data.mySeNetPriceStatus;
      }
      accessories.data.mySeNetPriceStatus = mySENetStatusGlobalAccessories;
    });

    if (accessories.children.length === 0) {
      const index = references.findIndex(elem => elem.data.category === accessories.data.category);
      references.splice(index, 1);
    }

    if (item.selectedServices.length > 0) {
      const csNode: TreeNode = BomTreeDataHelper.createTreeNode(this.translateService.instant('T_BOM_SERVICES'));
      let partnerDiscount = item.components
        .filter(ref => this.formatPartnerDiscount(ref.reference.partnerDiscount) !== '0.00%')
        .reduce((total, ref) => total + ref.reference.partnerDiscount, 0);
      let formattedPartnerDiscount = this.formatPartnerDiscount(partnerDiscount);
      references.push(csNode);
      item.selectedServices.forEach(servRef => {
        const extractedRef: ReferenceBom = new ReferenceBom();
        extractedRef.ref = servRef.reference.ref;
        extractedRef.count = servRef.quantity;
        extractedRef.deliveryTime = servRef.deliveryTime;
        extractedRef.description = this.translateService.instant(servRef.reference.description);
        extractedRef.price = servRef.reference.price;
        extractedRef.netPrice = servRef.reference.netPrice;
        extractedRef.totalPrice = servRef.reference.price * servRef.quantity;
        extractedRef.totalNetPrice = servRef.reference.netPrice * servRef.quantity;
        extractedRef.discount = item.discount;
        extractedRef.mySeNetPriceStatus = servRef.reference.mySeNetPriceStatus;
        const discountUnitPrice = UtilService.applyDiscountPrice(servRef.reference.price, extractedRef.discount);
        extractedRef.discountedPrice = discountUnitPrice * extractedRef.count;
        const discountUnitNetPrice = UtilService.applyDiscountPrice(servRef.reference.netPrice, extractedRef.discount);
        extractedRef.discountedNetPrice = discountUnitNetPrice * extractedRef.count;
        extractedRef.discountUnitPrice = discountUnitPrice;
        extractedRef.partnerDiscount = formattedPartnerDiscount;


        const child = {data: extractedRef, children: []};
        csNode.children.push(child);

        csNode.data.totalPrice += extractedRef.ref ? extractedRef.price * extractedRef.count : null;
        csNode.data.totalNetPrice += extractedRef.ref ? extractedRef.netPrice * extractedRef.count : null;
        this.setExtractDiscountedPrices(csNode, item.discount);

        csNode.data.mySeNetPriceStatus = extractedRef.mySeNetPriceStatus;
      });
    }
    return references;
  }

  /**
   *  Method to get the title of a BOM item
   * @param range of item
   * @param component one of the component of the item
   * @param index
   */
  private getTitleItemBom(range: SimplifiedRange, component: SwitchBoardComponent, index: number): string {
    if (!!range && range.switchboardLimit.max === 1) {
      return this.translateService.instant(range.nameKey);
    } else {
      return index + ' - ' + this.translateService.instant(component.name);
    }
  }

  /**
   * Transfer references to TreeNode
   * @param references
   * @param itemValue
   * @param discount
   * @param deliveryPolicy
   * @param item
   * @param ranges
   */
  private extractReferenceToTreeNode(references: TreeNode, itemValue, discount: number, deliveryPolicy: DeliveryPolicy, item: Item, ranges: Range[]) {
    let extractedRef: ReferenceBom;
    if (itemValue.rangeId !== undefined) {
      this.createNode(references, itemValue, discount, item, ranges);
    } else if (itemValue.reference) {
      // For cubicle options and accessories
      extractedRef = BomService.convertReferenceToReferenceBom(this.translateService, itemValue.reference, itemValue, discount, item.range, itemValue.quantity, this.rightsService.isPricesDetailsHidden(), false);
      const child = {data: extractedRef, children: []};
      references.children.push(child);
    } else if (itemValue.referenceBom) {
      extractedRef = this.bomService.extractReference(itemValue, discount, deliveryPolicy);
      const child = {data: extractedRef, children: []};
      references.children.push(child);
    }
  }

  /**
   * Allow one to display options of a cubicle or transformer component on the BOM array
   * @param {DeliveryPolicy} deliveryPolicy
   * @param {TreeNode} parentNode represents cubicle or transformer
   * @param {TreeNode} options list of component options
   * @param {SwitchBoardComponent} component cubicle or transformer
   * @param {Item} item
   * @param ranges list of known range
   */
  private extractOptionsToTreeNode(deliveryPolicy: DeliveryPolicy, parentNode: TreeNode, options: TreeNode,optionsParent: TreeNode,
                                   component: SwitchBoardComponent,
                                   item: Item,
                                   ranges: Range[]) {
    const categoryMap = new Map<string, TreeNode>();
    const groupedOptions = Object.groupBy(
      component.options.sort((a, b) => {
        if (!a.category && !b.category) return a.orderIndex - b.orderIndex;
        if (!a.category) return -1;
        if (!b.category) return 1;
        return (a.category.categoryOrderIndex ?? 0) - (b.category.categoryOrderIndex ?? 0);
      }),
      ({category}) => category?.id
    );
    Object.entries(groupedOptions).forEach(([categoryId, optionsSplited]) => {
      optionsSplited.forEach(option => {

        const hasSelectedReferenceValue = option.values.some(
          v => v.selected && v.reference
        );
        if (!hasSelectedReferenceValue) {
          return;
        }
        let currentParent = options;
        if (option.category != null) {
          currentParent = BomTreeDataHelper.getCategoryNode(options, categoryMap, this.translateService.instant(option.category.name));
          currentParent.data.optionCategory = true;
        }
        option.values.forEach(
          optValue => {
            // copy original value to not impact DB store item
            const copyOptValue = {...optValue};
            if (copyOptValue.selected && copyOptValue.reference) {
            if (!copyOptValue.quantity) {
              copyOptValue.quantity = component.quantity;
            } else {
              copyOptValue.quantity = component.quantity * copyOptValue.quantity;
            }
            this.extractReferenceToTreeNode(currentParent, copyOptValue, item.discount, deliveryPolicy, item, ranges);
              // Report quantity from the component to calculate the BOM
              this.setExtractPrices(optionsParent, copyOptValue, copyOptValue.quantity, item.discount, optionsParent.data.totalPrice, optionsParent.data.totalNetPrice);
              this.setExtractPrices(parentNode, copyOptValue, copyOptValue.quantity, item.discount, parentNode.data.totalPrice, parentNode.data.totalNetPrice, false);
              // For parent node, we update status just if it is an invalid status
              if (!UtilService.isValidMySEPriceFromStatus(currentParent.data.mySeNetPriceStatus)) {
                parentNode.data.mySeNetPriceStatus = currentParent.data.mySeNetPriceStatus;
              }
            }
          }
        );
        });
      });
  }

  /**
   * Create a node (a row and bom array )
   * @param {TreeNode} racine the first node with only the name of the component or product
   * @param product
   * @param {number} discount
   * @param {Item} item
   * @param ranges list of known range
   */
  private createNode(racine: TreeNode, product: IProduct, discount: number, item: Item, ranges: Range[]) {
    let extractedRef: ReferenceBom;
    if (item.range === null && item.selectedPackage) {
      let range;
      let switchBoardComponent = item.selectedPackage.switchboardComponents.find(value => value.rangeId !== null && value.rangeId !== undefined);
      if (switchBoardComponent !== null && switchBoardComponent !== undefined) {
        range = ranges.find(r => r.id.toString() === switchBoardComponent.rangeId[0]);
      }
      if (range !== null && range !== undefined) {
        item.range = range;
      }

    }
    extractedRef = BomService.convertComponentToReferenceBom(this.translateService, product, discount, item, this.rightsService.isPricesDetailsHidden(), false);
    const child = {data: extractedRef, children: []};
    racine.children.push(child);
  }

  /**
   * Set prices: total, net, discounted, discounted net and mySE status to node
   * @param node the node where we set prices
   * @param component component containing reference
   * @param quantity
   * @param discount
   * @param previousTotalPrice
   * @param previousNetPrice
   * @param updateMyseStatus
   * @private
   */
  private setExtractPrices(node: TreeNode, component: any, quantity: number, discount: number, previousTotalPrice?: number, previousNetPrice?: number,
                           updateMyseStatus: boolean = true) {
    if (previousTotalPrice != undefined) {
      node.data.totalPrice = previousTotalPrice;
    }
    if (previousNetPrice != undefined) {
      node.data.totalNetPrice = previousNetPrice;
    }

    const transformerOptions = this.translateService.instant('T_BOM_TRANSFORMER_OPTIONS');
    const cubicleOptions = this.translateService.instant('T_BOM_CUBICLE_OPTIONS');
    const reference = component.reference ? component.reference : component.referenceBom;

    if (reference && reference.price != null && node.data.totalPrice != 'containsNullPrices') {
      if (this.rightsService.isPricesDetailsHidden()) {
        if (this.rightsService.canApplyDiscount()) {
          node.children.forEach(treeNode => {
            if (treeNode.data.discountedPrice !== "" || treeNode.data.discountedPrice == 0) {
              treeNode.data.discountedPrice = '';
            }
            if (treeNode.data.discountUnitPrice !== "" || treeNode.data.discountUnitPrice == 0) {
              treeNode.data.discountUnitPrice = '';
            }
            // For option categories
            treeNode.children.forEach(subTreeNode => {
              if (subTreeNode.data.discountedPrice !== "" || subTreeNode.data.discountedPrice == 0) {
                subTreeNode.data.discountedPrice = '';
              }
              if (subTreeNode.data.discountUnitPrice !== "" || subTreeNode.data.discountUnitPrice == 0) {
                subTreeNode.data.discountUnitPrice = '';
              }
            });
          });
        }
        if (this.isNotOptionCategory(node, transformerOptions, cubicleOptions)) {
          node.data.partnerDiscount = this.formatPartnerDiscount(reference.partnerDiscount) || '0.00%';
          node.data.totalPrice += reference.price * quantity;
        }
        node.children.forEach(treeNode => {
          if (treeNode.data.totalPrice != null || treeNode.data.price != null) {
            treeNode.data.totalPrice = '';
            if (!this.isNotOptionCategory(node, transformerOptions, cubicleOptions)) {
              node.data.totalPrice = '';
            }
          }
          // For option categories
          treeNode.children.forEach(subTreeNode => {
            if (subTreeNode.data.totalPrice != null || subTreeNode.data.price != null) {
              subTreeNode.data.totalPrice = '';
              if (!this.isNotOptionCategory(treeNode, transformerOptions, cubicleOptions)) {
                treeNode.data.totalPrice = '';
              }
            }
          });
        });
        } else {
        node.data.totalPrice += reference.price * quantity;
        }
    } else if (reference && reference.price == null) {
      if (this.rightsService.isPricesDetailsHidden()) {
        if (this.isNotOptionCategory(node, transformerOptions, cubicleOptions)) {
          node.data.partnerDiscount = this.formatPartnerDiscount(reference.partnerDiscount) || '0.00%';
        }
        reference.price = reference.price ?? 0; // Set to 0 if null
        node.data.totalPrice += reference.price * quantity;
      } else {
        node.data.totalPrice = 'containsNullPrices';
      }
    }

    if (reference && reference.netPrice != null && node.data.totalNetPrice != 'containsNullPrices') {
      if (this.rightsService.isPricesDetailsHidden()) {
        if (this.rightsService.canApplyDiscount()) {
          node.children.forEach(treeNode => {
            if (treeNode.data.discountedNetPrice !== "" || treeNode.data.discountedNetPrice == 0) {
              treeNode.data.discountedNetPrice = '';
            }
            treeNode.children.forEach(subTreeNode => {
              if (subTreeNode.data.discountedNetPrice !== "" || subTreeNode.data.discountedNetPrice == 0) {
                subTreeNode.data.discountedNetPrice = '';
              }
            });

          });
        }
        if (this.isNotOptionCategory(node, transformerOptions, cubicleOptions)) {
          node.data.partnerDiscount = this.formatPartnerDiscount(reference.partnerDiscount) || '0.00%';
          node.data.totalNetPrice += reference.netPrice * quantity;
        }
        if (!this.isNotOptionCategory(node, transformerOptions, cubicleOptions)) {
          node.data.discountedNetPrice = '';
        }
        node.children.forEach(treeNode => {
          if (treeNode.data.totalNetPrice != null || treeNode.data.netPrice != null) {
            treeNode.data.totalNetPrice = '';
            if (!this.isNotOptionCategory(node, transformerOptions, cubicleOptions)) {
              node.data.totalNetPrice = '';
            }
          }
          treeNode.children.forEach(subTreeNode => {
            if (subTreeNode.data.totalNetPrice != null || subTreeNode.data.netPrice != null) {
              subTreeNode.data.totalNetPrice = '';
              if (!this.isNotOptionCategory(treeNode, transformerOptions, cubicleOptions)) {
                treeNode.data.totalNetPrice = '';
              }
            }
          });
        });
      } else {
          node.data.totalNetPrice += reference.netPrice * quantity;
        }
    } else if (reference && reference.netPrice == null) {
      if (this.rightsService.isPricesDetailsHidden()) {
        if (this.isNotOptionCategory(node, transformerOptions, cubicleOptions)) {
          node.data.partnerDiscount = node.data.optionCategory ? null : (this.formatPartnerDiscount(reference.partnerDiscount) || '0.00%');
        }
        reference.netPrice = reference.netPrice ?? 0; // Set to 0 if null
        node.data.totalNetPrice += reference.netPrice * quantity;
      } else {
        node.data.totalNetPrice = 'containsNullPrices';
      }
    }

    node.children.forEach(treeNode =>{
      if (treeNode.data.optionCategory) {
        treeNode.data.totalNetPrice = '';
        treeNode.data.totalPrice = '';
        treeNode.data.discountedPrice = '';
      }
    });



    if (this.rightsService.isPricesDetailsHidden()) {
      node.children.forEach(treeNode => {
        const {data} = treeNode;
        if (data.price === null) {
          data.totalPrice = 'containsNullPrices';
        }
        if (data.netPrice === null) {
          data.totalNetPrice = 'containsNullPrices';
        }
      });
    }

    this.setExtractDiscountedPrices(node, discount);
    if (updateMyseStatus) {
      this.setExtractMyseStatus(node, component);
    }
  }

  private isNotOptionCategory(node: TreeNode<any>, transformerOptions, cubicleOptions) {
    return node.data.category !== transformerOptions && node.data.category !== cubicleOptions && !node.data.optionCategory;
  }

  /**
   * Set discounted prices and discounted net prices to node
   * @param node
   * @param discount
   */
  private setExtractDiscountedPrices(node: TreeNode, discount: number) {
    node.data.discountedPrice = UtilService.applyDiscountPrice(node.data.totalPrice, discount);
    node.data.discountedNetPrice = UtilService.applyDiscountPrice(node.data.totalNetPrice, discount);
  }

  /**
   * Set mySE status to node
   * @param node
   * @param component
   */
  private setExtractMyseStatus(node: TreeNode, component: any) {
    node.data.mySeNetPriceStatus = UtilService.getMySEStatusFromObjectWithReference(component);
  }

  /**
   * Format the partner discount to a string with percentage
   * @param partnerDiscount
   * @private
   */
  private formatPartnerDiscount(partnerDiscount: number) {
    return (partnerDiscount * 100).toFixed(2) + '%';
  }

  /**
   * Get the category node if already created or create it if not exist, then return it
   * @param options
   * @param categoryMap
   * @param categoryId
   * @private
   */
  private static getCategoryNode(options: TreeNode, categoryMap: Map<string, TreeNode>, categoryId: string): TreeNode {
    let categoryNode = categoryMap.get(categoryId);
    if (!categoryNode) {
      categoryNode = BomTreeDataHelper.createTreeNode(categoryId);
      options.children.push(categoryNode);
      categoryMap.set(categoryId, categoryNode);
    }
    return categoryNode;
  }
}
